import { useQuery } from '@tanstack/react-query'
import { getOrgSettings } from './api/admin'

/**
 * 첫 비밀번호가 무엇인지 — **한 곳에서만 정한다.**
 *
 * 🔴 이번 주 **네 번째 사본 문제**다 (2026-09-12).
 *   `file-name-match` · `decodeUploadFileName` · 초기 비밀번호 규칙 · 그리고 이것.
 *   9/9 에 개별 등록(`EmployeeCreateDialog`)이 조직 설정을 읽도록 고쳤는데,
 *   **일괄 등록(`EmployeeBulkDialog`)은 「사번이 첫 비밀번호」로 하드코딩된 채였다.**
 *
 *   9/12 리허설에서 그게 실제로 터졌다 — 관리자가 그 문구를 읽고 직원에게 사번을
 *   알려 주면 **직원이 로그인을 못 한다.** 박지훈(E002)이 사번으로 로그인에 실패하고
 *   조직 공통 비밀번호로 성공했다.
 *
 * ★ 그래서 한쪽을 고치지 않고 **없앴다.** 같게 만들면 다음에 또 한쪽만 고쳐진다
 *   (`file-name-match.ts` 때와 같은 판단).
 *
 * ⚠ `branding` 이 아니라 `settings` 에서 읽는다. branding 은 로그인 전에도 부르는
 *   공개 API 라 거기에 넣으면 조직의 비밀번호 정책이 인증 없이 새어 나간다.
 */

export interface InitialPasswordRule {
  /** 사번이 곧 첫 비밀번호인 조직인가 (아니면 조직 공통 폴백) */
  byEmployeeNumber: boolean
}

/**
 * 조직 규칙을 읽는다. **아직 못 읽었으면 `null`** 이다.
 *
 * 🔴 `null` 일 때는 **아무 말도 안 한다.** 틀린 안내보다 없는 안내가 낫다 —
 *   관리자가 읽고 직원에게 그대로 전하는 문장이라서다.
 *
 * ⚠ `queryKey` 는 **같은 응답을 읽는 모든 화면**과 같아야 한다. 개별·일괄만이 아니라
 *   조직 설정(`admin/Settings.tsx`)도 같은 `GET /api/organization/settings` 를 읽고
 *   저장 뒤 그 키로 `invalidateQueries` 를 한다.
 *
 *   🔴 처음에 `['org-settings']` 로 두고 **「키도 같다」고 적었다. 사실이 아니었다** —
 *     설정 화면은 `['admin','org-settings']` 였다. 닫으려던 이음매를 안 닫고
 *     닫았다고 쓴 것이다 (2026-09-12 정정).
 *
 *   ⚠ 지금은 설정 화면이 `initialPasswordType` 을 못 바꿔서 **영향이 0** 이다
 *     (그 값은 슈퍼어드민만 정한다). 그래도 맞춰 둔다 — 나중에 그 칸이 생기면
 *     「저장했는데 등록 화면은 옛 규칙을 말하는」 상태가 조용히 생긴다.
 */
export function useInitialPasswordRule(): InitialPasswordRule | null {
  const { data } = useQuery({
    queryKey: ['admin', 'org-settings'],
    queryFn: async () => {
      const res = await getOrgSettings()
      return res.success ? res.data : undefined
    },
    staleTime: 5 * 60 * 1000,
  })
  if (!data) return null
  return { byEmployeeNumber: data.initialPasswordType === 'EMPLOYEE_NUMBER' }
}

/**
 * 한 사람을 등록할 때의 안내.
 *
 * 🔴 폴백일 때 **다음 행동까지 적는다.** 조직 공통 비밀번호는 조직을 만들 때 화면에
 *   한 번 뜨고 끝이라 관리자가 그 값을 모르는 경우가 흔하다. 그런데 막다른 길이
 *   아니다 — 직원 목록의 «임시 비밀번호» 버튼으로 그 사람 것만 따로 발급하면 된다.
 */
export function initialPasswordHintOne(rule: InitialPasswordRule, employeeNumber?: string): string {
  if (!rule.byEmployeeNumber) {
    return '첫 비밀번호는 조직 공통 초기 비밀번호입니다. 모르면 등록한 뒤 «임시 비밀번호» 버튼으로 발급해 알려 주세요.'
  }
  const n = employeeNumber?.trim()
  return n
    ? `첫 비밀번호는 사번(${n})입니다. 본인이 처음 로그인할 때 바꿉니다.`
    : '첫 비밀번호는 사번입니다. 본인이 처음 로그인할 때 바꿉니다.'
}

/**
 * 여러 명을 한꺼번에 등록할 때의 안내.
 *
 * 개별과 달리 사번을 하나로 못 박을 수 없다. 사번 기준 조직이라도 **사번이 없는
 * 사람은 공통 비밀번호**를 받으므로 그 갈림까지 적는다 (리허설의 최수연이 그 경우다).
 */
export function initialPasswordHintMany(rule: InitialPasswordRule): string {
  return rule.byEmployeeNumber
    ? '첫 비밀번호는 각자의 사번입니다. 사번을 안 적은 사람은 조직 공통 초기 비밀번호가 됩니다. 본인이 처음 로그인할 때 바꿉니다.'
    : '첫 비밀번호는 조직 공통 초기 비밀번호입니다. 모르면 등록한 뒤 직원 목록의 «임시 비밀번호» 버튼으로 발급해 알려 주세요.'
}
